import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Users, UserPlus, Crown, Shield, Ticket, ClipboardCheck,
  ClipboardX, Plus, X, ChevronRight, Zap,
} from "lucide-react";

export default function TeamsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["team-mine"],
    queryFn: () => api.get("/teams/mine").then((r) => r.data),
    refetchInterval: 20000,
  });
  const team = data?.team;
  const [showCreate, setShowCreate] = useState(false);

  const create = useMutation({
    mutationFn: (name) => api.post("/teams", { name }).then((r) => r.data),
    onSuccess: () => {
      toast.success("TEAM_CREATED");
      qc.invalidateQueries({ queryKey: ["team-mine"] });
      setShowCreate(false);
    },
    onError: (e) => toast.error("CREATE_FAILED", {
      description: e?.response?.data?.detail || e?.message,
    }),
  });

  if (isLoading) {
    return (
      <div className="p-6 font-mono text-[12px] text-zinc-500">LOADING_TEAM…</div>
    );
  }

  if (!team) {
    return (
      <div data-testid="teams-empty" className="p-4 space-y-3">
        <Header />
        <div className="border border-[#27272A] bg-[#0E0E0E] p-6">
          <p className="text-[13px] text-zinc-400 leading-relaxed">
            You aren&apos;t in a team yet. Bring your whole crew into a single
            shared MindStein workspace —{" "}
            <a
              href="/pricing"
              data-testid="teams-see-prices-link"
              className="text-emerald-300 underline hover:text-emerald-200"
            >
              see prices for teams
            </a>
            .
          </p>
          <button
            data-testid="teams-create-btn"
            onClick={() => setShowCreate(true)}
            className="mt-4 h-10 px-4 inline-flex items-center gap-2 border border-emerald-400 bg-emerald-400 text-black hover:bg-emerald-300 font-mono uppercase text-[11px] tracking-wider"
          >
            <Plus size={12} /> CREATE_TEAM
          </button>
        </div>
        {showCreate && (
          <CreateDialog
            onClose={() => setShowCreate(false)}
            onSubmit={(name) => create.mutate(name)}
            busy={create.isPending}
          />
        )}
      </div>
    );
  }

  return (
    <div data-testid="teams-page" className="p-4 space-y-4">
      <Header />
      <SummaryCards team={team} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <MembersTable team={team} />
        </div>
        <div className="space-y-3">
          <TickerCard team={team} />
          <SeatsCard team={team} />
          <RequestQueue teamId={team.id} />
        </div>
      </div>
      <MyRequestForm team={team} />
    </div>
  );
}

function Header() {
  return (
    <header>
      <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300 font-mono inline-flex items-center gap-2">
        <Users size={12} /> TEAMS · USAGE_MANAGEMENT
      </div>
      <h1 className="font-mono text-2xl tracking-tight mt-1">Teams</h1>
      <p className="text-[12px] text-zinc-500 mt-1 max-w-2xl leading-relaxed">
        Add seats, set per-member limits, approve extra-use requests, and watch
        the monthly bonus tick credit itself for every group of five active
        members. Every action is admin-gated.
      </p>
    </header>
  );
}

function SummaryCards({ team }) {
  const cards = [
    { label: "ACTIVE_SEATS", value: `${team.active_seats}/${team.total_seats}`, icon: Users },
    { label: "MONTHLY_BONUS_USES", value: team.monthly_bonus_uses ?? 0, icon: Zap },
    {
      label: "WELCOME_BONUS",
      value: team.welcome_bonus_credited ? "CREDITED" : "Pending 5 seats",
      icon: Ticket,
    },
    {
      label: "LAST_BONUS_MONTH",
      value: team.last_bonus_month || "—",
      icon: ClipboardCheck,
    },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((c) => (
        <div
          key={c.label}
          className="border border-[#27272A] bg-[#0E0E0E] p-3"
          data-testid={`teams-summary-${c.label.toLowerCase()}`}
        >
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1">
            <c.icon size={11} /> {c.label}
          </div>
          <div className="font-mono text-lg text-white mt-1 tabnum">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function MembersTable({ team }) {
  const qc = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState("");
  const invite = useMutation({
    mutationFn: () => api.post(`/teams/${team.id}/invite`,
      { email: inviteEmail }).then((r) => r.data),
    onSuccess: () => {
      toast.success("INVITED", { description: inviteEmail });
      setInviteEmail("");
      qc.invalidateQueries({ queryKey: ["team-mine"] });
    },
    onError: (e) => toast.error("INVITE_FAILED", {
      description: e?.response?.data?.detail || e?.message,
    }),
  });
  const promote = useMutation({
    mutationFn: (user_id) => api.post(`/teams/${team.id}/promote`,
      { user_id, monthly_limit: "50" }).then((r) => r.data),
    onSuccess: () => {
      toast.success("PROMOTED_TO_ADMIN");
      qc.invalidateQueries({ queryKey: ["team-mine"] });
    },
    onError: (e) => toast.error("PROMOTE_FAILED", {
      description: e?.response?.data?.detail || e?.message,
    }),
  });
  const setLimit = useMutation({
    mutationFn: ({ user_id, monthly_limit }) => api.post(`/teams/${team.id}/limit`,
      { user_id, monthly_limit }).then((r) => r.data),
    onSuccess: () => {
      toast.success("LIMIT_UPDATED");
      qc.invalidateQueries({ queryKey: ["team-mine"] });
    },
  });

  const isAdmin = ["owner", "admin"].includes(team?.me?.role);

  return (
    <section className="border border-[#27272A] bg-[#0E0E0E]">
      <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between gap-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          MEMBERS · {team.members?.length ?? 0}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <input
              data-testid="teams-invite-email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="teammate@lab.org"
              type="email"
              className="h-8 px-2 bg-[#0A0A0A] border border-[#27272A] text-[11px] font-mono text-white focus:border-emerald-400 outline-none w-56"
            />
            <button
              data-testid="teams-invite-btn"
              onClick={() => invite.mutate()}
              disabled={!inviteEmail || invite.isPending}
              className="h-8 px-3 inline-flex items-center gap-1 border border-emerald-400 bg-emerald-400 text-black hover:bg-emerald-300 font-mono uppercase text-[10px] tracking-wider disabled:opacity-50"
            >
              <UserPlus size={11} /> INVITE
            </button>
          </div>
        )}
      </div>
      {team.members?.length ? (
        <ul>
          {team.members.map((m) => (
            <li
              key={m.id}
              data-testid={`teams-member-${m.id}`}
              className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#161616] items-center text-[12px] font-mono"
            >
              <div className="col-span-4 text-white truncate">{m.email}</div>
              <div className="col-span-2 uppercase text-zinc-400 flex items-center gap-1">
                {m.role === "owner" ? <Crown size={11} className="text-amber-300" /> :
                 m.role === "admin" ? <Shield size={11} className="text-emerald-300" /> :
                 <Users size={11} className="text-zinc-500" />}
                {m.role}
              </div>
              <div className="col-span-2 text-zinc-400 uppercase">
                {m.status}
              </div>
              <div className="col-span-2 text-zinc-400 tabnum">
                {m.monthly_limit === "unlimited" ? "∞" : m.monthly_limit}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                {isAdmin && m.role === "member" && m.user_id && (
                  <>
                    <button
                      data-testid={`teams-promote-${m.id}`}
                      onClick={() => promote.mutate(m.user_id)}
                      className="h-6 px-2 border border-emerald-400/50 text-emerald-300 hover:bg-emerald-500/10 text-[10px] font-mono uppercase tracking-wider"
                      title="Promote to admin"
                    >
                      PROMOTE
                    </button>
                    <button
                      data-testid={`teams-limit-${m.id}`}
                      onClick={() => {
                        const v = window.prompt(
                          "Monthly limit for " + m.email + ' — enter a number or "unlimited":',
                          m.monthly_limit || "50",
                        );
                        if (v !== null) setLimit.mutate({ user_id: m.user_id, monthly_limit: v.trim() });
                      }}
                      className="h-6 px-2 border border-[#27272A] text-zinc-300 hover:border-emerald-400 hover:text-white text-[10px] font-mono uppercase tracking-wider"
                      title="Set monthly usage limit"
                    >
                      LIMIT
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="p-4 text-center text-[11px] font-mono text-zinc-500">
          NO_MEMBERS_YET
        </div>
      )}
    </section>
  );
}

function TickerCard({ team }) {
  const qc = useQueryClient();
  const tick = useMutation({
    mutationFn: () => api.post(`/teams/${team.id}/tick`).then((r) => r.data),
    onSuccess: (d) => {
      if (d.already_credited) {
        toast.info("ALREADY_CREDITED_THIS_MONTH");
      } else {
        toast.success("TICKER_CREDITED", {
          description: `+${d.credited} uses · ${d.active_seats} active seats`,
        });
      }
      qc.invalidateQueries({ queryKey: ["team-mine"] });
    },
  });
  const isAdmin = ["owner", "admin"].includes(team?.me?.role);
  return (
    <div className="border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2">
      <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-300 flex items-center gap-1">
        <Zap size={11} /> MONTHLY_TICKER
      </div>
      <p className="text-[11px] font-mono text-zinc-400 leading-relaxed">
        Adds <span className="text-white">+25 uses × ⌊{team.active_seats}/5⌋</span>{" "}
        this month. Idempotent — one credit per calendar month.
      </p>
      <button
        data-testid="teams-tick-btn"
        onClick={() => tick.mutate()}
        disabled={!isAdmin || tick.isPending}
        className="w-full h-8 border border-emerald-400 bg-emerald-400 text-black hover:bg-emerald-300 font-mono uppercase text-[10px] tracking-wider disabled:opacity-50"
      >
        {tick.isPending ? "…" : "RUN_MONTHLY_TICK"}
      </button>
    </div>
  );
}

function SeatsCard({ team }) {
  const qc = useQueryClient();
  const add = useMutation({
    mutationFn: (extra) => api.post(`/teams/${team.id}/seats`, { extra })
      .then((r) => r.data),
    onSuccess: (d) => {
      toast.success("SEATS_ADDED", { description: d.note });
      qc.invalidateQueries({ queryKey: ["team-mine"] });
    },
    onError: (e) => toast.error("ADD_SEATS_FAILED", {
      description: e?.response?.data?.detail || e?.message,
    }),
  });
  const isAdmin = ["owner", "admin"].includes(team?.me?.role);
  return (
    <div className="border border-[#27272A] bg-[#0E0E0E] p-3 space-y-2">
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1">
        <UserPlus size={11} /> ADD_SEATS
      </div>
      <p className="text-[11px] font-mono text-zinc-400 leading-relaxed">
        $49.99 per extra seat · 50 uses / 30d each. Billed via Stripe (MOCKED).
      </p>
      <button
        data-testid="teams-add-seats"
        onClick={() => {
          const v = window.prompt("How many extra seats to add?", "1");
          const n = parseInt(v, 10);
          if (n >= 1) add.mutate(n);
        }}
        disabled={!isAdmin || add.isPending}
        className="w-full h-8 border border-amber-400/60 text-amber-200 hover:bg-amber-400 hover:text-black font-mono uppercase text-[10px] tracking-wider disabled:opacity-50"
      >
        ADD_SEATS
      </button>
    </div>
  );
}

function RequestQueue({ teamId }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["team-requests", teamId],
    queryFn: () => api.get(`/teams/${teamId}/requests`).then((r) => r.data),
    refetchInterval: 15000,
  });
  const requests = data?.requests || [];
  const decide = useMutation({
    mutationFn: ({ req_id, action }) =>
      api.post(`/teams/${teamId}/requests/${req_id}/${action}`).then((r) => r.data),
    onSuccess: (_, v) => {
      toast.success(v.action === "approve" ? "APPROVED" : "REJECTED");
      qc.invalidateQueries({ queryKey: ["team-requests", teamId] });
    },
  });
  return (
    <div className="border border-[#27272A] bg-[#0E0E0E] p-3">
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1 mb-2">
        <ClipboardCheck size={11} /> REQUEST_QUEUE · {requests.filter((r) => r.status === "pending").length}
      </div>
      {requests.length === 0 ? (
        <div className="text-[11px] font-mono text-zinc-500">NO_REQUESTS</div>
      ) : (
        <ul className="space-y-1.5 max-h-96 overflow-y-auto">
          {requests.map((r) => (
            <li
              key={r.id}
              data-testid={`teams-request-${r.id}`}
              className={`border ${
                r.status === "pending" ? "border-amber-400/50" :
                r.status === "approved" ? "border-emerald-500/40" :
                "border-rose-500/40"
              } bg-[#0A0A0A] p-2`}
            >
              <div className="flex items-center justify-between gap-2 text-[10px] font-mono uppercase tracking-wider">
                <span className="text-white truncate">{r.member_email}</span>
                <span className={
                  r.status === "pending" ? "text-amber-300" :
                  r.status === "approved" ? "text-emerald-300" :
                  "text-rose-300"
                }>{r.status}</span>
              </div>
              <div className="text-[11px] font-mono text-zinc-300 mt-0.5">
                +{r.amount} uses · {r.created_at?.slice(0, 19)}
              </div>
              {r.reason && (
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5 line-clamp-2">
                  ↳ {r.reason}
                </div>
              )}
              {r.status === "pending" && (
                <div className="flex items-center gap-1 mt-1.5">
                  <button
                    data-testid={`teams-approve-${r.id}`}
                    onClick={() => decide.mutate({ req_id: r.id, action: "approve" })}
                    className="flex-1 h-6 border border-emerald-400 text-emerald-300 hover:bg-emerald-500/10 text-[10px] font-mono uppercase tracking-wider inline-flex items-center justify-center gap-1"
                  >
                    <ClipboardCheck size={10} /> APPROVE
                  </button>
                  <button
                    data-testid={`teams-reject-${r.id}`}
                    onClick={() => decide.mutate({ req_id: r.id, action: "reject" })}
                    className="flex-1 h-6 border border-rose-400 text-rose-300 hover:bg-rose-500/10 text-[10px] font-mono uppercase tracking-wider inline-flex items-center justify-center gap-1"
                  >
                    <ClipboardX size={10} /> REJECT
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MyRequestForm({ team }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState(10);
  const [reason, setReason] = useState("");
  const req = useMutation({
    mutationFn: () => api.post(`/teams/${team.id}/request-uses`,
      { amount, reason }).then((r) => r.data),
    onSuccess: () => {
      toast.success("REQUEST_SUBMITTED", { description: "Awaiting admin approval." });
      setReason("");
      qc.invalidateQueries({ queryKey: ["team-requests", team.id] });
    },
    onError: (e) => toast.error("REQUEST_FAILED", {
      description: e?.response?.data?.detail || e?.message,
    }),
  });
  return (
    <section className="border border-purple-500/30 bg-purple-500/5 p-3">
      <div className="text-[10px] font-mono uppercase tracking-wider text-purple-300 flex items-center gap-1 mb-2">
        <UserPlus size={11} /> REQUEST_MORE_USES
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          data-testid="teams-request-amount"
          type="number"
          min={1}
          max={500}
          value={amount}
          onChange={(e) => setAmount(Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 1)))}
          className="h-9 w-24 px-2 bg-[#0A0A0A] border border-[#27272A] text-[12px] font-mono text-white focus:border-purple-400 outline-none tabnum"
        />
        <input
          data-testid="teams-request-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional, max 400 chars)"
          maxLength={400}
          className="flex-1 h-9 px-2 bg-[#0A0A0A] border border-[#27272A] text-[12px] font-mono text-white focus:border-purple-400 outline-none"
        />
        <button
          data-testid="teams-request-submit"
          onClick={() => req.mutate()}
          disabled={req.isPending}
          className="h-9 px-3 border border-purple-400 bg-purple-400 text-black hover:bg-purple-300 font-mono uppercase text-[10px] tracking-wider disabled:opacity-50"
        >
          {req.isPending ? "…" : "SEND_REQUEST"}
        </button>
      </div>
    </section>
  );
}

function CreateDialog({ onClose, onSubmit, busy }) {
  const [name, setName] = useState("");
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md border border-emerald-400/60 bg-[#0A0A0A] p-5"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-300">
            NEW_TEAM
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X size={14} />
          </button>
        </div>
        <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
          TEAM_NAME
        </label>
        <input
          data-testid="teams-create-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Research Pod · Q4"
          maxLength={120}
          className="w-full h-10 px-2 bg-[#0E0E0E] border border-[#27272A] text-[13px] font-mono text-white focus:border-emerald-400 outline-none"
        />
        <button
          data-testid="teams-create-submit"
          onClick={() => name.trim() && onSubmit(name.trim())}
          disabled={!name.trim() || busy}
          className="mt-4 w-full h-10 border border-emerald-400 bg-emerald-400 text-black hover:bg-emerald-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
        >
          {busy ? "CREATING…" : "CREATE_TEAM"}
        </button>
        <p className="text-[10px] font-mono text-zinc-500 mt-2 leading-relaxed">
          You&apos;re seated as owner immediately. See prices for teams on the{" "}
          <a href="/pricing" className="text-emerald-300 underline hover:text-emerald-200">
            pricing page
          </a>
          .
        </p>
      </div>
    </div>
  );
}
